// Admin Undo shared confirmation modal.
//
// Mirrors the consequence-copy pattern of the existing PromptModals.
// Action-specific title + body + a free-text "Reason for undo (audit-
// visible)" textarea, server-validated to be at least 5 characters.
// Confirm is disabled until the trimmed reason meets the minimum so the
// 400 round-trip never happens on the happy path.
//
// The destructive Confirm button surfaces an explicit "CONFIRM: UNDO"
// label so an admin closing the modal by reflex can't fire the
// reversal. A red audit-visibility warning sits above the textarea —
// the reason field is read by all users with audit access (compliance
// review).

import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";

export const ADMIN_UNDO_MIN_REASON_LENGTH = 5;

export interface AdminUndoConfirmModalProps {
  open: boolean;
  onClose: () => void;
  /** Action-specific title, e.g. "Undo Section 9 Hand-off". */
  actionTitle: string;
  /** Action-specific paragraph describing what changes and which
   *  downstream caveats the admin must understand. Plain text or
   *  React node — the modal renders inside a single <p> block. */
  actionConsequence: ReactNode;
  /** Fired with the trimmed reason when the admin confirms. Errors
   *  thrown by the caller propagate; the modal stays open while
   *  isPending is true. */
  onConfirm: (reason: string) => void | Promise<void>;
  /** Drives the loading state of the Confirm button. */
  isPending: boolean;
}

export function AdminUndoConfirmModal({
  open,
  onClose,
  actionTitle,
  actionConsequence,
  onConfirm,
  isPending,
}: AdminUndoConfirmModalProps) {
  const [reason, setReason] = useState("");

  // Reset on close so the next open starts fresh — the alternative
  // (carrying the reason between opens) felt confusing during UX
  // review.
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= ADMIN_UNDO_MIN_REASON_LENGTH;
  const showLengthHint = reason.length > 0 && !reasonValid;

  const handleConfirm = () => {
    if (!reasonValid || isPending) return;
    void onConfirm(trimmed);
  };

  return (
    <Modal
      open={open}
      onClose={isPending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title={actionTitle}
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!reasonValid || isPending}
            loading={isPending}
          >
            CONFIRM: UNDO
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <p className="font-semibold text-status-error" role="alert">
          ⚠ This action is audit-logged and visible to compliance reviewers.
        </p>
        <div className="text-small text-gray-dark">{actionConsequence}</div>

        <label
          htmlFor="admin-undo-reason"
          className="block mt-space-2 text-small font-display uppercase tracking-label text-gold-shadow"
        >
          Reason for undo (audit-visible)
        </label>
        <textarea
          id="admin-undo-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border border-border-light rounded-md p-space-2 text-small focus:outline-none focus-visible:shadow-goldRing"
          placeholder={`Explain why this undo is necessary. Min. ${ADMIN_UNDO_MIN_REASON_LENGTH} characters.`}
          rows={3}
          disabled={isPending}
          aria-invalid={showLengthHint || undefined}
          aria-describedby={showLengthHint ? "admin-undo-reason-hint" : undefined}
        />
        {showLengthHint && (
          <p
            id="admin-undo-reason-hint"
            className="text-caption text-status-error"
            role="alert"
          >
            Reason must be at least {ADMIN_UNDO_MIN_REASON_LENGTH} characters.
          </p>
        )}
      </div>
    </Modal>
  );
}
